// src/components/customers/RoomModal.jsx
import { useEffect, useRef, useState } from "react";
import ColoredCommentsEditor from "./ColoredCommentsEditor";

/**
 * RoomModal Component
 *
 * A modal dialog for adding and editing measurement information.
 * Provides a clean, accessible form interface with dropdown selection and custom input.
 * Features responsive design that's wider on desktop and mobile-optimized.
 *
 * The Comments & Notes field supports per-selection text colour (red / green /
 * orange) via ColoredCommentsEditor. It hands back BOTH the plain `comments`
 * string and the `commentsSegments` array on the same change event — see the
 * data contract in src/utils/coloredText.js.
 *
 * @param {Object} props - Component props
 * @param {boolean} props.isOpen - Whether the modal is open
 * @param {Function} props.onClose - Function to close the modal
 * @param {Object} props.params - Room parameters (id, name, comments, commentsSegments, images)
 * @param {Function} props.onInputChange - Function to handle input changes
 * @param {Function} props.onSave - Function to save the room
 */
const RoomModal = ({ isOpen, onClose, params, onInputChange, onSave }) => {
  const nameSelectRef = useRef();
  const nameInputRef = useRef();
  const [isCustomInput, setIsCustomInput] = useState(false);

  // Predefined measurement area options as requested
  const nameOptions = [
    { value: "", label: "Select from options below or choose 'Custom'..." },
    { value: "Bedrooms", label: "Bedrooms" },
    { value: "Stairs and Landing", label: "Stairs and Landing" },
    { value: "Ground Floor", label: "Ground Floor" },
    { value: "Kitchen", label: "Kitchen" },
    { value: "Bathroom", label: "Bathroom" },
    { value: "custom", label: "✏️ Custom - Enter your own text" },
  ];

  // Check if current value is custom (not in predefined options)
  useEffect(() => {
    if (
      params.name &&
      !nameOptions.some((option) => option.value === params.name)
    ) {
      setIsCustomInput(true);
    } else {
      setIsCustomInput(params.name === "custom");
    }
  }, [params.name]);

  // Handle dropdown selection
  const handleDropdownChange = (e) => {
    const selectedValue = e.target.value;

    if (selectedValue === "custom") {
      setIsCustomInput(true);
      // Clear the name and focus on input
      onInputChange({ target: { id: "name", value: "" } });
      setTimeout(() => {
        if (nameInputRef.current) {
          nameInputRef.current.focus();
        }
      }, 100);
    } else {
      setIsCustomInput(false);
      onInputChange(e);
    }
  };

  // Handle custom input change
  const handleCustomInputChange = (e) => {
    onInputChange(e);
  };

  // Switch back to dropdown
  const switchToDropdown = () => {
    setIsCustomInput(false);
    onInputChange({ target: { id: "name", value: "" } });
    setTimeout(() => {
      if (nameSelectRef.current) {
        nameSelectRef.current.focus();
      }
    }, 100);
  };

  // Focus on name select when modal opens
  useEffect(() => {
    if (isOpen && nameSelectRef.current) {
      setTimeout(() => {
        nameSelectRef.current.focus();
      }, 100);
    }
  }, [isOpen]);

  // Handle form submission
  const handleSubmit = (e) => {
    e.preventDefault();
    onSave();
  };

  // Handle backdrop click to close modal
  const handleBackdropClick = (e) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  // Handle escape key to close modal
  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === "Escape" && isOpen) {
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener("keydown", handleEscape);
      document.body.style.overflow = "hidden"; // Prevent background scroll
    }

    return () => {
      document.removeEventListener("keydown", handleEscape);
      document.body.style.overflow = "unset";
    };
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  return (
    <div
      className="fixed inset-0 z-50 overflow-y-auto"
      role="dialog"
      aria-modal="true"
      aria-labelledby="room-modal-title"
    >
      {/* Backdrop */}
      <div
        className="fixed inset-0 bg-black bg-opacity-50 transition-opacity duration-300"
        onClick={handleBackdropClick}
        aria-hidden="true"
      />

      {/* Modal Container */}
      <div className="flex min-h-full items-center justify-center p-4">
        <div className="relative bg-white rounded-lg shadow-xl w-full max-w-2xl sm:max-w-3xl md:max-w-5xl lg:max-w-6xl xl:max-w-7xl 2xl:max-w-[1600px] max-h-[90vh] resize overflow-auto transform transition-all duration-300">
          {/* Modal Header */}
          <div className="flex items-center justify-between p-6 border-b border-gray-200">
            <h3
              id="room-modal-title"
              className="text-lg font-semibold text-gray-900 flex items-center"
            >
              {/* Measurement icon */}
              <svg
                className="w-6 h-6 mr-2 text-indigo-600"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z"
                />
              </svg>
              {params.id ? "Edit Measurement" : "Add Measurement"}
            </h3>

            {/* Close button */}
            <button
              type="button"
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600 transition-colors duration-200 p-1 rounded-full hover:bg-gray-100"
              aria-label="Close modal"
            >
              <svg
                className="w-6 h-6"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M6 18L18 6M6 6l12 12"
                />
              </svg>
            </button>
          </div>

          {/* Modal Body */}
          <form onSubmit={handleSubmit} className="p-8">
            {/* Grid Layout for larger screens */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              {/* Area Name Dropdown and Custom Input */}
              <div className="md:col-span-2">
                <label
                  htmlFor="name"
                  className="block text-sm font-medium text-gray-700 mb-2"
                >
                  Measurement Area <span className="text-red-500">*</span>
                </label>

                {!isCustomInput ? (
                  // Custom styled select dropdown
                  <div className="relative">
                    <select
                      ref={nameSelectRef}
                      id="name"
                      value={params.name || ""}
                      onChange={handleDropdownChange}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 transition-all duration-200 bg-white appearance-none cursor-pointer text-base"
                      required
                    >
                      {nameOptions.map((option) => (
                        <option
                          key={option.value}
                          value={option.value}
                          disabled={option.value === ""}
                        >
                          {option.label}
                        </option>
                      ))}
                    </select>

                    {/* Custom dropdown arrow */}
                    <div className="absolute inset-y-0 right-0 flex items-center px-3 pointer-events-none">
                      <svg
                        className="w-5 h-5 text-gray-400"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M19 9l-7 7-7-7"
                        />
                      </svg>
                    </div>
                  </div>
                ) : (
                  // Custom Input
                  <div className="flex gap-2">
                    <input
                      ref={nameInputRef}
                      id="name"
                      value={params.name || ""}
                      onChange={handleCustomInputChange}
                      className="flex-1 px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 transition-all duration-200 text-base"
                      placeholder="Enter your custom area name"
                      required
                    />
                    <button
                      type="button"
                      onClick={switchToDropdown}
                      className="px-4 py-3 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 focus:outline-none focus:ring-2 focus:ring-gray-500 transition-all duration-200"
                      title="Switch back to dropdown"
                    >
                      <svg
                        className="w-5 h-5"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M4 6h16M4 12h16M4 18h16"
                        />
                      </svg>
                    </button>
                  </div>
                )}

                <p className="mt-2 text-sm text-gray-500">
                  {!isCustomInput
                    ? "Select a measurement area from the dropdown or choose 'Custom' to enter your own"
                    : "Enter a custom area name or click the menu icon to return to dropdown"}
                </p>
              </div>

              {/* Comments/Notes Field */}
              <div className="md:col-span-2">
                <label
                  htmlFor="comments"
                  className="block text-sm font-medium text-gray-700 mb-2"
                >
                  Comments & Notes
                </label>
                <ColoredCommentsEditor
                  id="comments"
                  value={params.comments || ""}
                  segments={params.commentsSegments}
                  onChange={onInputChange}
                  rows={8}
                  maxLength={1000}
                  placeholder="Enter notes about measurements, materials, or special considerations..."
                />
                <div className="flex justify-between mt-2">
                  <p className="text-sm text-gray-500">
                    Optional notes — select any part of the text and use Red,
                    Green or Orange above to colour it
                  </p>
                  <span className="text-xs text-gray-400">
                    {(params.comments || "").length}/1000
                  </span>
                </div>
              </div>
            </div>

            {/* Form Actions */}
            <div className="flex flex-col sm:flex-row justify-end gap-3 mt-8 pt-6 border-t border-gray-200">
              {/* Cancel Button */}
              <button
                type="button"
                onClick={onClose}
                className="w-full sm:w-auto px-6 py-3 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-gray-500 focus:ring-offset-2 transition-all duration-200 font-medium text-base"
              >
                Cancel
              </button>

              {/* Save Button */}
              <button
                type="submit"
                className="w-full sm:w-auto px-8 py-3 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 transition-all duration-200 shadow-sm hover:shadow-md flex items-center justify-center font-medium text-base"
              >
                {/* Save icon */}
                <svg
                  className="w-5 h-5 mr-2"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M5 13l4 4L19 7"
                  />
                </svg>
                {params.id ? "Update Measurement" : "Add Measurement"}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default RoomModal;
